import { createSlice } from '@reduxjs/toolkit';
import type { RootState } from 'store';

import { apiSlice } from '../api/apiSlice';
import {
  AIanalyticsRequest,
  AIMessagePanelRequest,
  AIMessagePanelResponse,
  PatientAiSummaryResponse,
  StreamSuggestionResponse,
  SuggestedMessageRequest,
  SuggestionResponse
} from './aiAssistant.types';

const aiAssistantApi = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getAIMessagePanel: build.mutation<AIMessagePanelResponse, AIMessagePanelRequest>({
      query: (body) => ({
        body,
        headers: {
          'Cache-Control': 'no-cache'
        },
        method: 'POST',
        url: '/ai/message-panel'
      })
    }),
    getPatientAiSummary: build.query<PatientAiSummaryResponse, string>({
      query: (patientId) => ({
        method: 'GET',
        url: `/patients/${patientId}/ai-summary`
      })
    }),
    getStreamSuggestedResponse: build.mutation<StreamSuggestionResponse, SuggestedMessageRequest>({
      query: ({ body, force, signal }) => ({
        method: 'POST',
        url: `/ai/stream`,
        ...(force && {
          headers: {
            'Cache-Control': 'no-cache'
          }
        }),
        body,
        signal
      })
    }),
    getSuggestedResponse: build.mutation<SuggestionResponse, SuggestedMessageRequest>({
      query: ({ body, force, signal }) => ({
        method: 'POST',
        url: `/ai/suggestions`,
        ...(force && {
          headers: {
            'Cache-Control': 'no-cache'
          }
        }),
        body,
        signal
      })
    }),
    runMessagePanelInference: build.mutation<AIMessagePanelResponse, AIMessagePanelRequest>({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/ai/message-panel'
      })
    }),
    sendAnalytics: build.mutation<null, AIanalyticsRequest>({
      query: (body) => {
        return {
          body,
          headers: {
            'API-KEY': import.meta.env.VITE_API_KEY
          },
          method: 'POST',
          url: '/ai/analytics'
        };
      }
    })
  })
});

export const {
  useGetAIMessagePanelMutation,
  useGetPatientAiSummaryQuery,
  useGetStreamSuggestedResponseMutation,
  useGetSuggestedResponseMutation,
  useRunMessagePanelInferenceMutation,
  useSendAnalyticsMutation
} = aiAssistantApi;

const initialState = {
  hashKey: '',
  messageSuggestion: ''
};

const aiAssistantSlice = createSlice({
  extraReducers: (builder) => {
    builder.addMatcher(
      aiAssistantApi.endpoints.getStreamSuggestedResponse.matchFulfilled,
      (state, action) => {
        if (action.payload) {
          state.hashKey = action.payload.info.key;
        }
      }
    );
    builder.addMatcher(
      aiAssistantApi.endpoints.getSuggestedResponse.matchFulfilled,
      (state, action) => {
        if (action.payload) {
          state.hashKey = action.payload.info.key;
        }
      }
    );
  },
  initialState: initialState,
  name: 'aiAssistant',
  reducers: {
    resetAIasistantState: () => initialState,
    setHashKey: (state, action) => {
      state.hashKey = action.payload;
    },
    setMessageSuggestion: (state, action) => {
      state.messageSuggestion = action.payload;
    }
  }
});

export default aiAssistantSlice.reducer;

export const { resetAIasistantState, setHashKey, setMessageSuggestion } = aiAssistantSlice.actions;

export const selectAiAssistant = (state: RootState) => state.aiAssistant;
